// Single source of truth for everything Carry & Basis needs per token —
// ONE implied_volatility_smile call per token per poll returns the full
// multi-tenor curve (DF/T/ATMF/Future per tenor, from surface.expiry.tenors)
// AND spot (market.spot), from one consistent snapshot — so the whole curve
// (spot/forward/DF) can't disagree the way it could when sourced from
// latest_vol_surface + a separate candlestick spot poll (use-vol-surface.ts
// + use-live-spot.ts, both removed).
//
// Confirmed live 2026-07-14: surface.expiry.tenors is the FULL multi-tenor
// surface regardless of which `expiry` is requested (even an arbitrary
// non-listed date returns the identical tenor set/DF/Future values) — so
// the `expiry` param just needs to be a plausible date, not a real listed
// one, and one call per poll is enough for the whole curve.
//
// DF_base (build-carry-curve.ts's baseDf) is computed from this same
// response's own DF_usd/Future/spot via covered interest parity
// (DF_base = DF_usd * Future/Spot) — not a separately-sourced backend
// figure.
//
// Bare ticker works for `base` (no _USDC suffix needed, unlike
// latest_vol_surface/futures_snapshot) for BTC/ETH/SOL/XRP. AVAX errors
// with a backend Internal error on every base/quote/expiry combination
// tried — not a param issue on this end — so AVAX is dropped from the
// Carry & Basis board until that's fixed (ticket needed).
import { useContext, useEffect, useRef, useState } from 'react';

import { WebSocketContext } from '../websocket-context/websocket-provider';
import { parseTokenPair } from './use-available-tokens';

export type VolSurfaceTenors = Record<string, Record<string, number>>;

const POLL_MS = 30_000;

// Doesn't need to resolve to a real listed expiry (confirmed above) — just
// a plausible forward date, recomputed each poll so it never goes stale.
const arbitraryExpiryParam = (): string => {
  const d = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  const stamp = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 8, 0, 0),
  ).toISOString();
  return stamp.replace(/\.\d{3}Z$/, '.000Z');
};

export const useImpliedVolSmile = (
  token: string,
): {
  tenors: VolSurfaceTenors | null;
  spot: number | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: number | null;
} => {
  const { sendMessage, subscribeToChannel, unsubscribeFromChannel, isConnected } =
    useContext(WebSocketContext);
  const [tenors, setTenors] = useState<VolSurfaceTenors | null>(null);
  const [spot, setSpot] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  const wsRef = useRef({ sendMessage, subscribeToChannel, unsubscribeFromChannel });
  useEffect(() => {
    wsRef.current = { sendMessage, subscribeToChannel, unsubscribeFromChannel };
  });

  useEffect(() => {
    if (!token || !isConnected) return;
    let cancelled = false;

    const fetchSmile = () => {
      const ws = wsRef.current;
      if (!ws.sendMessage || !ws.subscribeToChannel) return;

      // Split the wire identifier into base/quote — the engine's
      // implied_volatility_smile resolves the underlying from base+quote, so a
      // joined `base:"SOL_USDC"` with a hardcoded `quote:"USD"` is rejected
      // (-32603, deribit.underlying_enum). parseTokenPair gives BTC->USD,
      // SOL_USDC->SOL/USDC, TRUMP_USDT->TRUMP/USDT. (IM-1906)
      const { base, quote } = parseTokenPair(token);

      const reqId = `IVS_${token}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      let settled = false;
      const finish = () => {
        settled = true;
        const unsub = wsRef.current.unsubscribeFromChannel;
        if (unsub) {
          try {
            unsub(reqId);
          } catch {
            /* ignore */
          }
        }
      };

      ws.subscribeToChannel(reqId, (raw: unknown) => {
        if (cancelled || settled) return;
        const resp = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
        if (resp?.error) {
          setError(`implied_volatility_smile error: ${JSON.stringify(resp.error)}`);
          setIsLoading(false);
          finish();
          return;
        }
        const result = resp?.result as Record<string, unknown> | undefined;
        const surface = result?.surface as Record<string, unknown> | undefined;
        const expiryBlock = surface?.expiry as Record<string, unknown> | undefined;
        const nextTenors = expiryBlock?.tenors;
        const market = result?.market as Record<string, unknown> | undefined;
        const nextSpot = market?.spot;

        if (!nextTenors || typeof nextTenors !== 'object') {
          setError('implied_volatility_smile: unexpected response shape');
          setIsLoading(false);
          finish();
          return;
        }
        setTenors(nextTenors as VolSurfaceTenors);
        setSpot(typeof nextSpot === 'number' && Number.isFinite(nextSpot) ? nextSpot : null);
        setError(null);
        setIsLoading(false);
        setLastUpdated(Date.now());
        finish();
      });

      ws.sendMessage(
        JSON.stringify({
          jsonrpc: '2.0',
          id: reqId,
          method: 'implied_volatility_smile',
          params: {
            base,
            quote,
            expiry: arbitraryExpiryParam(),
            strikes: [],
            deltas: [0.5],
            model: 'svi_jw',
          },
        }),
      );

      setTimeout(() => {
        if (cancelled || settled) return;
        setError('implied_volatility_smile timed out');
        setIsLoading(false);
        finish();
      }, 15000);
    };

    setIsLoading(true);
    fetchSmile();
    const interval = setInterval(fetchSmile, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [token, isConnected]);

  return { tenors, spot, isLoading, error, lastUpdated };
};
